import * as React from "react";
import { Grid } from "../../grid/grid/grid.mjs";
import { GridColumn } from "../../grid/grid-column/grid-column.mjs";
import { isOnScreen, scrollToElement } from "../../helpers/helper/index.mjs";
import { PropsChecker } from "../../helpers/props-checker/props-checker.mjs";
import debounce from "debounce";
const CollapseLayerGridContext = React.createContext({
  activeCollapse: null,
  getPortal: () => void 0,
  setActiveCollapse: () => null
});
const CollapseLayerGrid = (props) => {
  return /* @__PURE__ */ React.createElement(
    PropsChecker,
    {
      componentName: "CollapseLayerGrid",
      componentProps: props,
      defaultValueName: "defaultActiveCollapse",
      valueName: "activeCollapse",
      onChangeNames: "onActiveCollapseChange"
    },
    /* @__PURE__ */ React.createElement(CollapseLayerGridInner, { ...props })
  );
};
class CollapseLayerGridInner extends React.Component {
  constructor(props) {
    super(props);
    this.gridNode = null;
    this.resizeHandler = () => {
      const windowWidth = document.documentElement && document.documentElement.clientWidth;
      if (windowWidth !== this.state.windowWidth) {
        this.setState(
          {
            renderContainers: false,
            windowWidth
          },
          () => {
            this.calculateContainerPositions();
          }
        );
      }
    };
    this.scrollTo = (id) => {
      if (!isOnScreen(id)) {
        scrollToElement(id);
      }
    };
    this.setContainerColumnState = (index, element) => {
      if (element && this.state.containerRefs[index] !== element) {
        this.setState((prevState) => {
          const containerRefs = [...prevState.containerRefs];
          containerRefs[index] = element;
          return {
            containerRefs
          };
        });
      }
    };
    this.setActiveCollapse = (id) => {
      if (typeof this.props.activeCollapse === "undefined") {
        this.setState({
          activeCollapse: id
        });
      }
      if (this.props.onActiveCollapseChange) {
        this.props.onActiveCollapseChange(id);
      }
    };
    this.getElementPortal = (element) => {
      const { containerRefs, columnContainerMap } = this.state;
      const actualGrid = this.gridNode;
      if (!actualGrid) {
        return;
      }
      const domNodeArray = [].slice.call(actualGrid.children);
      const columnArray = domNodeArray.filter((node) => {
        return !node.className.includes("collapse-layer-grid__dropdown");
      });
      const elementColumn = columnArray.find((el) => {
        return el.contains(element);
      });
      if (elementColumn) {
        const indexOfColumn = columnArray.indexOf(elementColumn);
        return containerRefs[columnContainerMap[indexOfColumn].id];
      }
    };
    this.state = {
      activeCollapse: props.defaultActiveCollapse || null,
      columnContainerMap: [],
      containerPositions: [],
      containerRefs: [],
      gridInitialized: false,
      gridRows: [],
      renderContainers: false,
      windowWidth: null
    };
    this.debouncedResizeHandler = debounce(this.resizeHandler, 100);
  }
  componentDidMount() {
    this.calculateContainerPositions();
    this.initGrid();
    this.setState({
      windowWidth: document.documentElement && document.documentElement.clientWidth
    });
    window.addEventListener("resize", this.debouncedResizeHandler);
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.debouncedResizeHandler);
    this.debouncedResizeHandler.clear();
  }
  componentDidUpdate(prevProps, prevState) {
    if (Array.isArray(prevProps.children) && Array.isArray(this.props.children)) {
      const prevKeys = prevProps.children.filter((child) => React.isValidElement(child)).map((child) => child.key);
      const currentKeys = this.props.children.filter((child) => React.isValidElement(child)).map((child) => child.key);
      const sameKeys = prevKeys.filter((key) => currentKeys.includes(key));
      if (sameKeys.join() !== prevKeys.join()) {
        this.setState(
          {
            renderContainers: false
          },
          () => {
            this.calculateContainerPositions();
          }
        );
      }
    }
    const prevActiveCollapse = this.props.onActiveCollapseChange && typeof this.props.activeCollapse !== "undefined" ? prevProps.activeCollapse : prevState.activeCollapse;
    if (this.activeCollapse && prevActiveCollapse !== this.activeCollapse) {
      this.scrollTo(this.activeCollapse);
    }
  }
  get activeCollapse() {
    return this.props.onActiveCollapseChange && typeof this.props.activeCollapse !== "undefined" ? this.props.activeCollapse : this.state.activeCollapse;
  }
  getGridAlignment() {
    if (!this.gridNode) {
      return null;
    }
    switch (getComputedStyle(this.gridNode).getPropertyValue("align-items")) {
      case "flex-end":
        return "bottom";
      case "center":
        return "middle";
      default:
        return "top";
    }
  }
  getElementComparePosition(element, comparePosition) {
    switch (comparePosition) {
      case "bottom":
        return element.offsetTop + element.offsetHeight;
      case "middle":
        return element.offsetTop + Math.ceil(element.offsetHeight / 2);
      default:
        return element.offsetTop;
    }
  }
  calculateContainerPositions() {
    if (!this.gridNode) {
      return;
    }
    const actualGrid = this.gridNode;
    const domNodeArray = [].slice.call(actualGrid.children);
    let currentTop = null;
    let rowId = 0;
    const columnContainerMap = [];
    const gridRows = [];
    const containerPositions = [];
    const comparePosition = this.getGridAlignment();
    let nodeIndex = 0;
    for (const element of domNodeArray) {
      const elementComparePosition = this.getElementComparePosition(element, comparePosition);
      if (currentTop !== elementComparePosition && (Math.abs((currentTop || 0) - elementComparePosition) > 2 || Math.abs((currentTop || 0) - elementComparePosition) === 0)) {
        if (currentTop !== null) {
          containerPositions.unshift(nodeIndex);
        }
        currentTop = rowId = elementComparePosition;
        gridRows.unshift(rowId);
      }
      columnContainerMap.push({
        element,
        id: rowId
      });
      nodeIndex++;
    }
    containerPositions.unshift(domNodeArray.length);
    this.setState({
      columnContainerMap,
      containerPositions,
      gridRows,
      renderContainers: true
    });
  }
  initGrid() {
    this.setState(
      {
        gridInitialized: true
      },
      () => {
        if (this.activeCollapse) {
          this.scrollTo(this.activeCollapse);
        }
      }
    );
  }
  cleanChildren(props) {
    return React.Children.map(props.children, (child) => {
      if (React.isValidElement(child) && child.type === GridColumn) {
        return child;
      }
    });
  }
  BEM() {
    const classArray = ["collapse-layer-grid"];
    if (this.activeCollapse) {
      classArray.push("is-active");
    }
    if (this.props.className) {
      classArray.push(this.props.className);
    }
    return classArray.join(" ");
  }
  getChildrenWithContainers() {
    const { containerPositions, gridRows } = this.state;
    const childrenWithContainers = [];
    React.Children.toArray(this.props.children).filter((child) => {
      return React.isValidElement(child);
    }).forEach((child, index) => {
      childrenWithContainers.push(child);
      const containerIndex = index + 1;
      if (containerPositions.includes(containerIndex)) {
        const gridPosition = gridRows[containerPositions.indexOf(containerIndex)];
        const container = /* @__PURE__ */ React.createElement(
          GridColumn,
          {
            id: "".concat(this.props.id, "-container-").concat(gridPosition),
            key: "".concat(this.props.id, "-container-").concat(gridPosition),
            className: "collapse-layer-grid__dropdown",
            width: ["xs-12"],
            elementRef: this.setContainerColumnState.bind(this, gridPosition)
          }
        );
        childrenWithContainers.push(container);
      }
    });
    return childrenWithContainers;
  }
  render() {
    const { renderContainers } = this.state;
    const gridNodeRef = (el) => this.gridNode = el;
    const contextValue = {
      activeCollapse: this.activeCollapse,
      getPortal: this.getElementPortal,
      setActiveCollapse: this.setActiveCollapse
    };
    return /* @__PURE__ */ React.createElement(CollapseLayerGridContext.Provider, { value: contextValue }, /* @__PURE__ */ React.createElement(Grid, { ...this.props, className: this.BEM(), gridRef: gridNodeRef }, renderContainers ? this.getChildrenWithContainers() : this.props.children));
  }
}
export {
  CollapseLayerGrid,
  CollapseLayerGridContext,
  CollapseLayerGridInner
};
